import React, { ReactNode, useLayoutEffect, useRef } from 'react';
import styled from '@emotion/styled';

/**
 * 한 줄 고정 글 — 넘치면 글자를 줄여 한 줄에 맞추고, 최소 크기(기본 12px, ui-guide 캡션 최소)에서도 넘칠 때만 말줄임(…).
 * 잘렸을 때만 title 속성으로 전체 글을 보여 준다(길게 누르기·마우스 올리기).
 *
 * - 기본 글자 크기·줄 높이는 CSS(font-size, line-height·height 는 고정 길이)로 준다. 여기서는 inline font-size 만 바꾼다
 *   → 글자가 작아져도 줄 높이는 그대로라 카드 높이가 변하지 않는다.
 * - 한 번에 계산: 넘친 비율로 크기를 추정(0.5px 단위 내림)한 뒤 넘치면 0.5px 씩 몇 번만 보정. 매 프레임 재지 않는다.
 * - 다시 재는 때: 글이 바뀔 때(기본 크기부터), 칸 폭이 바뀔 때(공용 ResizeObserver 하나), 웹 글꼴 로딩이 끝났을 때 한 번.
 */

const MIN_STEP_PX: number = 0.5;
const MAX_CORRECTIONS: number = 6;

type Fitter = () => void;

const fitters: Map<Element, Fitter> = new Map();
const lastWidths: WeakMap<Element, number> = new WeakMap();
let sharedObserver: ResizeObserver | null = null;
let fontsHooked: boolean = false;

/** 로컬 dev 에서만 — 측정 횟수를 세어 스크롤 중 반복 측정이 없는지 확인할 수 있게 */
const countMeasure = (): void => {
    if (!import.meta.env.DEV) return;
    const w = window as unknown as { __fitOneLineMeasures?: number };
    w.__fitOneLineMeasures = (w.__fitOneLineMeasures ?? 0) + 1;
};

const observer = (): ResizeObserver | null => {
    if (typeof ResizeObserver === 'undefined') return null;
    if (!sharedObserver) {
        sharedObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
            entries.forEach((entry: ResizeObserverEntry) => {
                const width: number = Math.round(entry.contentRect.width);
                // 높이만 바뀐 건(글자 크기 조정 등) 무시 — 폭이 바뀔 때만 다시 맞춘다
                if (lastWidths.get(entry.target) === width) return;
                lastWidths.set(entry.target, width);
                fitters.get(entry.target)?.();
            });
        });
    }
    if (!fontsHooked && typeof document !== 'undefined' && document.fonts?.ready) {
        fontsHooked = true;
        document.fonts.ready.then(() => fitters.forEach((fit: Fitter) => fit())).catch(() => undefined);
    }
    return sharedObserver;
};

/** el 을 한 줄에 맞춘다. 반환: 최소 크기에서도 넘쳐 말줄임이 됐는지 */
export const fitElementToOneLine = (el: HTMLElement, minPx: number): boolean => {
    el.style.fontSize = '';
    countMeasure();
    const available: number = el.clientWidth;
    if (available === 0) return false; // 숨겨진 칸 — 보일 때 폭이 바뀌며 다시 맞춘다
    if (el.scrollWidth <= available) return false;
    const base: number = parseFloat(getComputedStyle(el).fontSize);
    if (!(base > minPx)) return true;
    let size: number = Math.max(minPx, Math.floor((base * available) / el.scrollWidth / MIN_STEP_PX) * MIN_STEP_PX);
    el.style.fontSize = `${size}px`;
    let corrections: number = 0;
    while (el.scrollWidth > el.clientWidth && size > minPx && corrections < MAX_CORRECTIONS) {
        size = Math.max(minPx, size - MIN_STEP_PX);
        el.style.fontSize = `${size}px`;
        corrections += 1;
    }
    return el.scrollWidth > el.clientWidth;
};

const Line = styled.div`
    display: block;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export interface FitOneLineProps {
    /** 전체 글 — 잘렸을 때 title 로 보여 주고, 바뀌면 기본 크기부터 다시 맞춘다 */
    text: string;
    /** 보여 줄 내용(없으면 text) — 앞에 다른 조각이 붙는 줄(예: 병원명 · 요약)용 */
    children?: ReactNode;
    className?: string;
    minPx?: number;
    style?: React.CSSProperties;
    /** 줄 종류 표시(data-line) — 화면 점검용 */
    line?: string;
}

const FitOneLine = ({ text, children, className, minPx = 12, style, line }: FitOneLineProps) => {
    const ref = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const el: HTMLDivElement | null = ref.current;
        if (!el) return undefined;
        const fit: Fitter = () => {
            const truncated: boolean = fitElementToOneLine(el, minPx);
            if (truncated) el.title = text;
            else el.removeAttribute('title');
        };
        fit();
        fitters.set(el, fit);
        const ro: ResizeObserver | null = observer();
        lastWidths.set(el, Math.round(el.getBoundingClientRect().width));
        ro?.observe(el);
        return () => {
            ro?.unobserve(el);
            fitters.delete(el);
        };
    }, [text, minPx]);

    return (
        <Line ref={ref} className={className} style={style} data-line={line}>
            {children ?? text}
        </Line>
    );
};

export default FitOneLine;
